import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Users, Clock, Shield, AlertTriangle, Scale, Heart, Gavel } from 'lucide-react';

export default function TermsPage() {
  return (
    <Layout>
      <div className="container-app py-8 md:py-12 max-w-3xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Conditions Générales d&apos;Utilisation</h1>
          <p className="text-muted-foreground">Oknî - Banque du Temps</p>
          <p className="text-sm text-muted-foreground mt-2">Dernière mise à jour : 14 février 2026</p>
        </div>

        <div className="space-y-6">
          {/* Article 1 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">1</span>
                Objet
              </h2>
              <p className="text-muted-foreground">
                Les présentes Conditions Générales d&apos;Utilisation (CGU) régissent l&apos;accès et l&apos;utilisation de la plateforme Oknî, banque du temps pour Ottignies-Louvain-la-Neuve et ses environs.
              </p>
              <p className="mt-3 p-3 rounded-xl bg-primary/10 text-primary font-medium">
                Principe fondamental : 1 heure de service rendu = 1 crédit-temps, quelle que soit la nature du service.
              </p>
            </CardContent>
          </Card>

          {/* Article 2 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">2</span>
                Éditeur
              </h2>
              <ul className="space-y-2 text-muted-foreground">
                <li><strong>Structure :</strong> Oknî</li>
                <li><strong>Adresse :</strong> Brabant wallon, Belgique (adresse complète communiquée prochainement)</li>
                <li><strong>Email :</strong> <a href="/contact" className="text-primary underline" data-testid="legal-contact-link">formulaire de contact</a></li>
              </ul>
            </CardContent>
          </Card>

          {/* Article 3 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">3</span>
                Acceptation
              </h2>
              <p className="text-muted-foreground">
                L&apos;inscription sur la plateforme implique l&apos;acceptation pleine et entiere des présentes CGU. Si vous n&apos;acceptez pas ces conditions, veuillez ne pas utiliser le service.
              </p>
            </CardContent>
          </Card>

          {/* Article 4 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">4</span>
                Inscription
              </h2>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside ml-2">
                <li><strong>Age minimum :</strong> 18 ans revolus</li>
                <li><strong>Modes d&apos;inscription :</strong> email/mot de passe ou Google OAuth</li>
                <li>Les informations fournies doivent être exactes et tenues à jour</li>
                <li><strong>Bonus de bienvenue :</strong> 10 crédits-temps offerts à l&apos;inscription</li>
                <li>L&apos;utilisateur est responsable de la confidentialité de ses identifiants</li>
                <li>La suppression du compte est possible à tout moment depuis les paramètres</li>
              </ul>
            </CardContent>
          </Card>

          {/* Article 5 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">5</span>
                Fonctionnement
              </h2>
              
              <h3 className="font-medium mt-4 mb-2">5.1 Principe</h3>
              <p className="text-muted-foreground ml-2">
                Oknî est une banque du temps basée sur l&apos;échange de services. Chaque heure de service rendu équivaut a 1 crédit-temps, indépendamment de la nature du service.
              </p>

              <h3 className="font-medium mt-4 mb-2">5.2 Annonces</h3>
              <p className="text-muted-foreground ml-2">
                Les utilisateurs peuvent publier des annonces de type &quot;Offre&quot; (ce qu&apos;ils proposent) et &quot;Besoin&quot; (ce qu&apos;ils recherchent). Un systeme de matching automatique facilite les mises en relation.
              </p>

              <h3 className="font-medium mt-4 mb-2">5.3 Échanges</h3>
              <p className="text-muted-foreground ml-2">
                Les crédits-temps sont transférés automatiquement à la complétion d&apos;un échange. Le demandeur est débité et le prestataire crédité.
              </p>

              <h3 className="font-medium mt-4 mb-2">5.4 Solde insuffisant</h3>
              <p className="text-muted-foreground ml-2">
                Un échange ne peut être initié si le demandeur n&apos;a pas suffisamment de crédits-temps.
              </p>
            </CardContent>
          </Card>

          {/* Article 6 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">6</span>
                Obligations des utilisateurs
              </h2>
              <p className="text-muted-foreground mb-3">Chaque utilisateur s&apos;engage a :</p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside ml-2">
                <li>Utiliser la plateforme de maniere loyale et respectueuse</li>
                <li>Ne pas publier de contenu illegal, diffamatoire ou discriminatoire</li>
                <li>Ne pas utiliser le service à des fins commerciales ou de travail au noir</li>
                <li>Honorer les engagements pris lors des échanges</li>
                <li>Ne pas contourner le systeme de crédits-temps</li>
                <li>Signaler tout abus ou comportement suspect</li>
              </ul>
            </CardContent>
          </Card>

          {/* Article 7 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">7</span>
                Responsabilité
              </h2>
              
              <h3 className="font-medium mt-4 mb-2">7.1 Role d&apos;intermédiaire</h3>
              <p className="text-muted-foreground ml-2">
                Oknî agit uniquement comme intermédiaire technique mettant en relation les utilisateurs. La plateforme n&apos;est pas partie aux échanges réalisés entre membres.
              </p>

              <h3 className="font-medium mt-4 mb-2">7.2 Responsabilité individuelle</h3>
              <p className="text-muted-foreground ml-2">
                Chaque utilisateur est responsable des services qu&apos;il propose et des eventuels dommages causes. Il est conseille de vérifier sa couverture en responsabilité civile.
              </p>

              <h3 className="font-medium mt-4 mb-2">7.3 Limitation</h3>
              <p className="text-muted-foreground ml-2">
                Oknî ne garantit pas la disponibilite continue du service et ne peut être tenu responsable des dommages indirects lies a son utilisation.
              </p>
            </CardContent>
          </Card>

          {/* Article 8 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">8</span>
                Moderation
              </h2>
              <p className="text-muted-foreground mb-3">
                L&apos;equipe de moderation peut prendre les mesures suivantes en cas de non-respect des CGU :
              </p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside ml-2">
                <li>Suppression de contenu inapproprie</li>
                <li>Suspension temporaire ou definitive du compte</li>
                <li>Ajustement des crédits-temps en cas d&apos;abus</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                L&apos;utilisateur concerne sera notifie de toute action prise a son encontre.
              </p>
            </CardContent>
          </Card>

          {/* Article 9 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">9</span>
                Propriete intellectuelle
              </h2>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside ml-2">
                <li>La plateforme Oknî (code, design, marque) est la propriete de l&apos;editeur</li>
                <li>Les contenus publies par les utilisateurs restent leur propriete</li>
                <li>En publiant du contenu, l&apos;utilisateur accorde a Oknî une licence d&apos;affichage non exclusive</li>
              </ul>
            </CardContent>
          </Card>

          {/* Article 10 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">10</span>
                Données personnelles
              </h2>
              <p className="text-muted-foreground">
                Le traitement des données personnelles est decrit dans notre{' '}
                <Link to="/privacy" className="text-primary hover:underline">
                  Politique de Confidentialité
                </Link>
                , qui fait partie integrante des présentes CGU.
              </p>
            </CardContent>
          </Card>

          {/* Article 11 */}
          <Card className="rounded-2xl border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" />
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">11</span>
                Nature non commerciale
              </h2>
              <p className="text-muted-foreground mb-3">
                Oknî est un projet communautaire a but non lucratif. A ce titre :
              </p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside ml-2">
                <li>Les crédits-temps n&apos;ont <strong>aucune valeur marchande</strong></li>
                <li>Les crédits-temps ne peuvent <strong>pas être échanges contre de l&apos;argent</strong></li>
                <li>L&apos;utilisation de la plateforme est <strong>entierement gratuite</strong></li>
              </ul>
            </CardContent>
          </Card>

          {/* Article 12 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">12</span>
                Droit applicable et litiges
              </h2>
              <p className="text-muted-foreground mb-3">
                Les présentes CGU sont soumises au droit belge.
              </p>
              <p className="text-muted-foreground">
                En cas de litige, les parties s&apos;engagent a rechercher une solution amiable avant tout recours judiciaire. A defaut d&apos;accord, les tribunaux du Brabant Wallon seront seuls competents.
              </p>
            </CardContent>
          </Card>

          {/* Article 13 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">13</span>
                Modification des CGU
              </h2>
              <p className="text-muted-foreground">
                Oknî se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés de toute modification significative. L&apos;utilisation continue du service après notification vaut acceptation des nouvelles conditions.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
